<template>
  <FormKit
    v-model="formattedAddress"
    type="autocomplete"
    name="address"
    aria-label="Breakdown Location"
    :label="label ?? ''"
    :placeholder="placeholder ?? 'Enter location'"
    :options="getSuggestions"
    prefixIcon="search"
    :wrapperClass="wrapperClass ?? 'flex block'"
    :outerClass="outerClass ?? '$remove:pb-4'"
    listboxButtonClass="$reset hidden"
    selectIconClass="$reset hidden"
    :innerClass="innerClass ?? '$remove:md:max-w-[300px] $remove:md:px-4 gap-2 md:px-3'"
    inputClass="$remove:px-3 font-sans !font-normal !leading-none text-base pr-3 pl-0 placeholder-racq-grey"
    prefixIconClass="$remove:bg-gradient-to-b $remove:border-gray-400 $remove:border-r $remove:[&>svg]:max-w-[1em] $remove:[&>svg]:max-h-[1em] flex-wrap content-center w-6 [&>svg]:max-w-6 [&>svg]:max-h-6 [&>svg]:w-6 [&>svg]:h-6 text-racq-light-grey"
    listboxClass="font-sans !font-normal !leading-none text-base"
    messagesClass="$reset list-none flex ml-0 pl-0"
    validation="required"
    :validationMessages="{
      required: requiredValidationMessage,
    }"
    validationVisibility="submit"
    @input="selectSuggestion"
  />
</template>

<script setup lang="ts">
// Add this line to reference the Google Maps types
/// <reference types="@types/google.maps" />

import { computed } from 'vue';
import { FormKit } from '@formkit/vue';
import { IDropdownItem } from '@/interfaces';
import { useGoogleMapStore } from '@/store';

const props = defineProps<{
  label?: string;
  outerClass?: string;
  innerClass?: string;
  wrapperClass?: string;
  placeholder?: string;
  requiredValidationMessage?: string;
}>();

const googleMapStore = useGoogleMapStore();
const formattedAddress = computed(() => googleMapStore.formattedAddress);
const requiredValidationMessage = computed(() => props.requiredValidationMessage ?? 'Please provide a valid address');

const emit = defineEmits(['selected']);

async function getSuggestions({ search }: { search: string }): Promise<IDropdownItem[]> {
  try {
    if (!search || search.length < 3) return [];

    // Create a session token.
    const token = new google.maps.places.AutocompleteSessionToken();
    // Add an initial request body.
    let request: google.maps.places.AutocompleteRequest = {
      input: search,
      origin: { lat: -27.5866031, lng: 153.1018371 },
      includedRegionCodes: ['au'], // only allow au address
      language: 'en-AU',
      sessionToken: token,
    };

    if (googleMapStore.suburbPostcodeMode) request.includedPrimaryTypes = ['locality', 'postal_code'];

    const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
    return suggestions
      .sort((a) => {
        return a.placePrediction?.secondaryText?.text?.includes('QLD') ? -1 : 1;
      })
      .map((suggestion: google.maps.places.AutocompleteSuggestion) => ({
        label: suggestion.placePrediction?.text.toString() ?? '',
        value: {
          prediction: suggestion.placePrediction,
          label: suggestion.placePrediction?.text.toString() ?? '',
        },
      }));
  } catch (error) {
    console.warn('Unable to lookup: ', error);
    return [];
  }
}

const selectSuggestion = async (suggestion: { prediction: google.maps.places.PlacePrediction; label: string }) => {
  if (!suggestion?.prediction || !(suggestion?.prediction instanceof google.maps.places.PlacePrediction)) return;
  try {
    const place = suggestion.prediction.toPlace();
    await place.fetchFields({ fields: ['location', 'formattedAddress', 'id', 'addressComponents'] });

    const formattedAddress = googleMapStore.suburbPostcodeMode ? suggestion.label : place.formattedAddress;
    googleMapStore.setFormattedAddress(formattedAddress ?? '');
    googleMapStore.setLatitude(place.location?.lat() ?? 0);
    googleMapStore.setLongitude(place.location?.lng() ?? 0);
    googleMapStore.setAddressStateFromAddressComponents(place.addressComponents);

    document.getElementsByName('address')?.[0]?.blur();
    emit('selected', { lat: place.location?.lat() ?? 0, lng: place.location?.lng() ?? 0 });
  } catch (error) {
    console.warn('Unable to fetch place details: ', error);
  }
};
</script>
